import { ArrowUpRight, ShieldCheck, AlertCircle } from "lucide-react";

const Tag = ({ label, variant }) => {
  const styles =
    variant === "bank"
      ? "bg-blue-50 text-[#002FA7] border border-blue-200"
      : "bg-amber-50 text-amber-800 border border-amber-200";
  return (
    <span className={`${styles} px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold`}>
      {label}
    </span>
  );
};

const VerifiedBadge = ({ verified }) => {
  if (verified) {
    return (
      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold">
        <ShieldCheck className="w-3 h-3" /> Verified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-[#F8F9FA] text-[#4B5563] border border-[#E5E7EB] px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold">
      <AlertCircle className="w-3 h-3" /> RBI basic
    </span>
  );
};

const RolePill = ({ role }) => {
  const isRecovery = role.toLowerCase().includes("recovery");
  const isLegal = role.toLowerCase().includes("legal");
  const cls = isRecovery
    ? "bg-red-50 text-red-700 border border-red-200"
    : isLegal
    ? "bg-blue-50 text-[#002FA7] border border-blue-200"
    : "bg-[#F1F5F9] text-[#4B5563] border border-[#E5E7EB]";
  return (
    <span className={`${cls} px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold whitespace-nowrap`}>
      {role.replace(" Officer", " Off.")}
    </span>
  );
};

export const InstitutionTable = ({ items, loading, onSelect }) => {
  if (loading) {
    return (
      <div data-testid="table-loading" className="border border-[#0A0A0A] bg-white p-16 text-center">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#4B5563]">
          Loading directory…
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div data-testid="table-empty" className="border border-[#0A0A0A] bg-white p-16 text-center">
        <div className="font-display font-bold text-xl mb-2">No matching institutions</div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#4B5563]">
          Try resetting your filters
        </div>
      </div>
    );
  }

  return (
    <div data-testid="institution-table" className="border border-[#0A0A0A] bg-white overflow-hidden">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-[#F8F9FA]">
            <tr className="border-b-2 border-[#0A0A0A]">
              <th className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] w-[28%]">
                Institution
              </th>
              <th className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Type</th>
              <th className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Category</th>
              <th className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">HQ</th>
              <th className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Roles indexed</th>
              <th className="p-4 w-[1%]" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr
                key={it.id}
                data-testid={`institution-row-${it.short_name || it.id}`}
                onClick={() => onSelect(it.id)}
                className="border-b border-[#E5E7EB] cursor-pointer hover:bg-[#F1F5F9] transition-colors"
              >
                <td className="p-4 align-top">
                  <div className="font-display font-bold text-[15px] leading-tight">{it.name}</div>
                  {it.short_name && (
                    <div className="font-mono text-[11px] text-[#4B5563] mt-1">
                      {it.short_name} · {it.rbi_license_no}
                    </div>
                  )}
                </td>
                <td className="p-4 align-top">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag label={it.type} variant={it.type === "Bank" ? "bank" : "nbfc"} />
                    <VerifiedBadge verified={it.has_detailed_contacts} />
                  </div>
                </td>
                <td className="p-4 align-top text-sm">{it.category}</td>
                <td className="p-4 align-top text-sm">
                  <div>{it.hq_city}</div>
                  <div className="text-[#4B5563] text-xs">{it.hq_state}</div>
                </td>
                <td className="p-4 align-top">
                  <div className="flex flex-wrap gap-1 max-w-[260px]">
                    {it.roles.length === 0 ? (
                      <span className="font-mono text-[10px] text-[#9CA3AF]">no contacts</span>
                    ) : it.roles.map((r) => (
                      <RolePill key={r} role={r} />
                    ))}
                  </div>
                </td>
                <td className="p-4 align-top text-right">
                  <ArrowUpRight className="w-4 h-4 text-[#4B5563] group-hover:text-[#002FA7]" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden">
        {items.map((it) => (
          <button
            key={it.id}
            data-testid={`institution-card-${it.short_name || it.id}`}
            type="button"
            onClick={() => onSelect(it.id)}
            className="w-full text-left border-b border-[#E5E7EB] p-4 hover:bg-[#F1F5F9] transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="font-display font-bold text-base leading-tight">{it.name}</div>
                <div className="font-mono text-[11px] text-[#4B5563] mt-1">
                  {it.short_name ? `${it.short_name} · ` : ""}{it.hq_city}, {it.hq_state}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <Tag label={it.type} variant={it.type === "Bank" ? "bank" : "nbfc"} />
                <VerifiedBadge verified={it.has_detailed_contacts} />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {it.roles.length === 0 ? (
                <span className="font-mono text-[10px] text-[#9CA3AF]">no contacts on file</span>
              ) : it.roles.map((r) => (
                <RolePill key={r} role={r} />
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
